import React from "react";
import { createPortal } from "react-dom";
import {
  SaveOrShareOverlay,
  SaveOrShareCard,
  SaveOrShareHeader,
  SaveOrSharePreview,
  SaveOrSharePreviewImage,
  SaveOrShareHashtagPromo,
  SaveOrShareBulkNote,
  SaveOrShareTitle,
  SaveOrShareBody,
  SaveOrShareButton,
  SaveOrShareIcon,
  SaveOrShareCancel,
} from "./styled";

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const VideoboardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="10" rx="1" />
    <path d="M7 21h10M12 17v4" />
  </svg>
);

interface SaveOrShareModalProps {
  visible: boolean;
  onSaveToDevice: () => void;
  onCancel: () => void;
  buttonColor?: string;
  plural?: boolean;
  /** Image URL (data URL or blob URL) to show as preview above the options */
  previewImageUrl?: string;
  /** Custom hashtag to promote (e.g. "MyMomento" - displayed as #mymomento) */
  shareHashtag?: string;
  /** Shown under the title for bulk / ZIP save flows */
  bulkSaveNote?: string;
  /** Team “Send to Videoboard” client option: extra action on save/share sheet. */
  showSendToVideoboard?: boolean;
  onSendToVideoboard?: () => void | Promise<void>;
  sendToVideoboardLoading?: boolean;
  /** When true the button is shown in a "already sent" disabled state. */
  videoboardAlreadySent?: boolean;
}

export const SaveOrShareModal: React.FC<SaveOrShareModalProps> = ({
  visible,
  onSaveToDevice,
  onCancel,
  buttonColor,
  plural = false,
  previewImageUrl,
  shareHashtag: shareHashtagProp,
  bulkSaveNote,
  showSendToVideoboard = false,
  onSendToVideoboard,
  sendToVideoboardLoading = false,
  videoboardAlreadySent = false,
}) => {
  if (!visible) return null;

  const noun = plural ? "images" : "image";
  const teamColor = buttonColor;
  const hashtag = (shareHashtagProp?.replace(/^#/, "").trim() || "MyMomento").toLowerCase();

  const modalContent = (
    <SaveOrShareOverlay onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <SaveOrShareCard onClick={(e) => e.stopPropagation()}>
        <SaveOrShareHeader $teamColor={teamColor}>
          <SaveOrShareTitle>
            Save your
            {" "}
            {noun}
          </SaveOrShareTitle>
        </SaveOrShareHeader>

        {previewImageUrl && (
          <SaveOrSharePreview>
            <SaveOrSharePreviewImage src={previewImageUrl} alt="Preview" />
          </SaveOrSharePreview>
        )}

        {!bulkSaveNote ? (
          <SaveOrShareHashtagPromo>
            Share using #
            {hashtag}
          </SaveOrShareHashtagPromo>
        ) : null}

        {bulkSaveNote ? (
          <SaveOrShareBulkNote>{bulkSaveNote}</SaveOrShareBulkNote>
        ) : null}

        <SaveOrShareBody>
          <SaveOrShareButton $variant="primary" $teamColor={teamColor} onClick={onSaveToDevice}>
            <SaveOrShareIcon>
              <DownloadIcon />
            </SaveOrShareIcon>
            Download
          </SaveOrShareButton>

          {showSendToVideoboard && onSendToVideoboard ? (
            <SaveOrShareButton
              $variant="outline"
              $teamColor={teamColor}
              disabled={!!sendToVideoboardLoading || videoboardAlreadySent}
              onClick={() => {
                onSendToVideoboard();
              }}
            >
              <SaveOrShareIcon>
                <VideoboardIcon />
              </SaveOrShareIcon>
              {sendToVideoboardLoading ? "Sending…" : videoboardAlreadySent ? "Already Sent" : "Send to Videoboard"}
            </SaveOrShareButton>
          ) : null}

          <SaveOrShareCancel onClick={onCancel}>
            Cancel
          </SaveOrShareCancel>
        </SaveOrShareBody>
      </SaveOrShareCard>
    </SaveOrShareOverlay>
  );

  return createPortal(modalContent, document.body);
};
